import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Button } from '@/components/ui/Button'
import { Input, Select, Textarea } from '@/components/ui/Field'
import type { Collection, ListingStatus, SpecRow, Watch, WatchCondition, MovementKind } from '@/types'

interface Props {
  /** Present when editing; absent when posting a new piece. */
  watch?: Watch | null
  collections: Collection[]
  saving?: boolean
  onSubmit: (data: Partial<Watch>) => void | Promise<void>
  onCancel?: () => void
}

/** A brand-new listing starts here — sensible defaults, nothing invented. */
function blank(): Partial<Watch> {
  return {
    reference: '',
    title: '',
    title_vi: '',
    collection: '',
    maison: '',
    price: 0,
    old_price: 0,
    movement: 'automatic',
    case_material: '',
    case_size: '',
    water_resistance: '',
    strap: '',
    year: String(new Date().getFullYear()),
    condition: 'new',
    image: '',
    gallery: [],
    specs: [],
    description: '',
    description_vi: '',
    stock: 1,
    status: 'draft',
    is_featured: false,
    views: 0,
  }
}

/** A titled band inside the form, so a long form still reads as four short ones. */
function Fieldset({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-5 py-8 border-b border-border last:border-b-0">
      <legend className="eyebrow">{title}</legend>
      {children}
    </fieldset>
  )
}

/**
 * Post / edit a listing — the heart of the owner studio.
 *
 * English is the base language of the data; the Vietnamese fields are optional
 * and fall back to English on the storefront (see `tf()` in src/i18n).
 */
export function ListingForm({ watch, collections, saving = false, onSubmit, onCancel }: Props) {
  const { t, tf } = useI18n()
  const [form, setForm] = useState<Partial<Watch>>(() => ({ ...blank(), ...(watch ?? {}) }))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [galleryText, setGalleryText] = useState(() => (watch?.gallery ?? []).join('\n'))

  const set = <K extends keyof Watch>(key: K, value: Watch[K]) => setForm((f) => ({ ...f, [key]: value }))

  const collectionOptions = useMemo(
    () => [
      { value: '', label: '—' },
      ...collections.map((c) => ({ value: c.slug, label: tf(c, 'name') })),
    ],
    [collections, tf],
  )

  const specs: SpecRow[] = Array.isArray(form.specs) ? form.specs : []
  const setSpec = (i: number, patch: Partial<SpecRow>) =>
    set('specs', specs.map((row, idx) => (idx === i ? { ...row, ...patch } : row)))

  const submit = (status: ListingStatus) => (e: FormEvent) => {
    e.preventDefault()
    const next: Record<string, string> = {}
    if (!form.title?.trim()) next.title = t('studio.errorTitle')
    if (!Number(form.price) || Number(form.price) <= 0) next.price = t('studio.errorPrice')
    if (!form.image?.trim()) next.image = t('studio.errorImage')
    setErrors(next)
    if (Object.keys(next).length) return

    void onSubmit({
      ...form,
      status,
      price: Number(form.price) || 0,
      old_price: Number(form.old_price) || 0,
      stock: Number(form.stock) || 0,
      gallery: galleryText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      specs: specs.filter((s) => s.label.trim() && s.value.trim()),
      created_at: form.created_at ?? new Date().toISOString(),
    })
  }

  return (
    <form
      data-upbo-id="listing-form"
      data-upbo-type="form"
      data-upbo-label="Listing form"
      className="flex flex-col"
      onSubmit={submit((form.status as ListingStatus) ?? 'draft')}
    >
      {/* ── Identity ───────────────────────────────────────────────────── */}
      <Fieldset title={t('studio.sectionIdentity')}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            name="title"
            label={t('studio.fieldTitle')}
            value={form.title ?? ''}
            error={errors.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <Input
            name="title_vi"
            label={t('studio.fieldTitleVi')}
            hint={t('common.optional')}
            value={form.title_vi ?? ''}
            onChange={(e) => set('title_vi', e.target.value)}
          />
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Input
            name="reference"
            label={t('studio.fieldReference')}
            value={form.reference ?? ''}
            onChange={(e) => set('reference', e.target.value)}
          />
          <Select
            name="collection"
            label={t('studio.fieldCollection')}
            options={collectionOptions}
            value={form.collection ?? ''}
            onChange={(e) => set('collection', e.target.value)}
          />
          <Input
            name="maison"
            label={t('studio.fieldMaison')}
            value={form.maison ?? ''}
            onChange={(e) => set('maison', e.target.value)}
          />
        </div>
      </Fieldset>

      {/* ── Specification ──────────────────────────────────────────────── */}
      <Fieldset title={t('studio.sectionSpecs')}>
        <div className="grid sm:grid-cols-3 gap-4">
          <Select
            name="movement"
            label={t('studio.fieldMovement')}
            options={[
              { value: 'automatic', label: t('movement.automatic') },
              { value: 'manual', label: t('movement.manual') },
              { value: 'quartz', label: t('movement.quartz') },
            ]}
            value={form.movement ?? 'automatic'}
            onChange={(e) => set('movement', e.target.value as MovementKind)}
          />
          <Input
            name="case_material"
            label={t('studio.fieldCase')}
            value={form.case_material ?? ''}
            onChange={(e) => set('case_material', e.target.value)}
          />
          <Input
            name="case_size"
            label={t('studio.fieldSize')}
            value={form.case_size ?? ''}
            onChange={(e) => set('case_size', e.target.value)}
          />
        </div>
        <div className="grid sm:grid-cols-4 gap-4">
          <Input
            name="water_resistance"
            label={t('studio.fieldWater')}
            value={form.water_resistance ?? ''}
            onChange={(e) => set('water_resistance', e.target.value)}
          />
          <Input
            name="strap"
            label={t('studio.fieldStrap')}
            value={form.strap ?? ''}
            onChange={(e) => set('strap', e.target.value)}
          />
          <Input
            name="year"
            label={t('studio.fieldYear')}
            value={form.year ?? ''}
            onChange={(e) => set('year', e.target.value)}
          />
          <Select
            name="condition"
            label={t('condition.label')}
            options={[
              { value: 'new', label: t('condition.new') },
              { value: 'pre_owned', label: t('condition.pre_owned') },
              { value: 'vintage', label: t('condition.vintage') },
            ]}
            value={form.condition ?? 'new'}
            onChange={(e) => set('condition', e.target.value as WatchCondition)}
          />
        </div>

        {/* Free-form spec rows: whatever this particular piece deserves. */}
        <div className="flex flex-col gap-3">
          {specs.map((row, i) => (
            <div key={i} className="flex items-end gap-3">
              <Input
                fieldSize="sm"
                placeholder="Calibre"
                value={row.label}
                onChange={(e) => setSpec(i, { label: e.target.value })}
              />
              <Input
                fieldSize="sm"
                placeholder="TM-24 in-house, 68 h reserve"
                value={row.value}
                onChange={(e) => setSpec(i, { value: e.target.value })}
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('common.delete')}
                onClick={() => set('specs', specs.filter((_, idx) => idx !== i))}
              >
                <Trash2 size={15} strokeWidth={1.5} />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="w-fit"
            icon={<Plus size={14} strokeWidth={1.5} />}
            onClick={() => set('specs', [...specs, { label: '', value: '' }])}
          >
            {t('studio.addSpec')}
          </Button>
        </div>
      </Fieldset>

      {/* ── Images ─────────────────────────────────────────────────────── */}
      <Fieldset title={t('studio.sectionMedia')}>
        <Input
          name="image"
          label={t('studio.fieldImage')}
          value={form.image ?? ''}
          error={errors.image}
          onChange={(e) => set('image', e.target.value)}
        />
        <div className="grid sm:grid-cols-[1fr_auto] gap-5 items-start">
          <Textarea
            name="gallery"
            label={t('studio.fieldGallery')}
            hint={t('studio.fieldGalleryHint')}
            rows={4}
            value={galleryText}
            onChange={(e) => setGalleryText(e.target.value)}
          />
          {/* Live proof that the URL actually points at a photograph. */}
          {form.image ? (
            <img
              src={form.image}
              alt=""
              className="h-32 w-32 object-cover rounded-squircle border border-border"
            />
          ) : null}
        </div>
      </Fieldset>

      {/* ── Copy ───────────────────────────────────────────────────────── */}
      <Fieldset title={t('studio.sectionCopy')}>
        <Textarea
          name="description"
          label={t('studio.fieldDescription')}
          value={form.description ?? ''}
          onChange={(e) => set('description', e.target.value)}
        />
        <Textarea
          name="description_vi"
          label={t('studio.fieldDescriptionVi')}
          hint={t('common.optional')}
          value={form.description_vi ?? ''}
          onChange={(e) => set('description_vi', e.target.value)}
        />
      </Fieldset>

      {/* ── Publishing ─────────────────────────────────────────────────── */}
      <Fieldset title={t('studio.sectionPublishing')}>
        <div className="grid sm:grid-cols-4 gap-4">
          <Input
            name="price"
            type="number"
            min={0}
            label={t('studio.fieldPrice')}
            value={String(form.price ?? 0)}
            error={errors.price}
            onChange={(e) => set('price', Number(e.target.value))}
          />
          <Input
            name="old_price"
            type="number"
            min={0}
            label={t('studio.fieldOldPrice')}
            hint={t('studio.fieldOldPriceHint')}
            value={String(form.old_price ?? 0)}
            onChange={(e) => set('old_price', Number(e.target.value))}
          />
          <Input
            name="stock"
            type="number"
            min={0}
            label={t('studio.fieldStock')}
            value={String(form.stock ?? 0)}
            onChange={(e) => set('stock', Number(e.target.value))}
          />
          <Select
            name="status"
            label={t('studio.fieldStatus')}
            options={[
              { value: 'draft', label: t('status.draft') },
              { value: 'published', label: t('status.published') },
              { value: 'reserved', label: t('status.reserved') },
              { value: 'sold', label: t('status.sold') },
            ]}
            value={form.status ?? 'draft'}
            onChange={(e) => set('status', e.target.value as ListingStatus)}
          />
        </div>

        <label className="inline-flex items-center gap-3 text-sm text-text cursor-pointer w-fit">
          <input
            type="checkbox"
            checked={!!form.is_featured}
            onChange={(e) => set('is_featured', e.target.checked)}
            className="h-4 w-4 accent-[var(--color-primary)]"
          />
          {t('studio.fieldFeatured')}
        </label>
      </Fieldset>

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <div className="sticky bottom-0 bg-bg/95 backdrop-blur-sm border-t border-border py-4 flex flex-wrap items-center gap-3">
        <Button size="lg" loading={saving} onClick={submit('published')}>
          {t('studio.publish')}
        </Button>
        <Button variant="outline" size="lg" disabled={saving} onClick={submit('draft')}>
          {t('studio.saveDraft')}
        </Button>
        {onCancel && (
          <Button variant="ghost" size="lg" disabled={saving} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
      </div>
    </form>
  )
}
